import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { useWpSetReasonMutation } from './StepReason.graphql';
import { type GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';

type FormValues = Pick<GqlWarrantProgram, 'forEmployees' | 'forBoardMembers' | 'forAdvisors' | 'forInvestors'>;

export const WpStepReason = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    'id' | 'companyId' | 'forEmployees' | 'forBoardMembers' | 'forAdvisors' | 'forInvestors'
  >;
}) => {
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const [doSetReason, { loading }] = useWpSetReasonMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      forEmployees: warrantProgram.forEmployees,
      forBoardMembers: warrantProgram.forBoardMembers,
      forAdvisors: warrantProgram.forAdvisors,
      forInvestors: warrantProgram.forInvestors,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async data => {
    try {
      await doSetReason({
        variables: {
          companyId: warrantProgram.companyId,
          input: {
            id: warrantProgram.id,
            ...data,
            forAdvisors: data.forAdvisors ?? false,
            forInvestors: data.forInvestors ?? false,
          },
        },
      });
      await stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Reason" />
        </Typography>

        <Alert>
          <Trans id="When issuing warrants, you must state the purpose. By selecting one or more of these points, a correctly formulated purpose is created that can be approved by the Swedish Companies Registration Office" />
        </Alert>

        <Controller
          control={control}
          name="forEmployees"
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={event => onChange(event.target.checked)}
              label={<Trans id="For employees" />}
            />
          )}
        />
        <Controller
          control={control}
          name="forBoardMembers"
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={event => onChange(event.target.checked)}
              label={<Trans id="For board members" />}
            />
          )}
        />
        <Controller
          control={control}
          name="forAdvisors"
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              {...field}
              checked={Boolean(value)}
              onChange={event => onChange(event.target.checked)}
              label={<Trans id="For advisors" />}
            />
          )}
        />

        <Controller
          control={control}
          name="forInvestors"
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={event => onChange(event.target.checked)}
              label={<Trans id="For investors" />}
            />
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
